import React, { useState } from 'react';
import { X, Ruler } from 'lucide-react';

interface SizeGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  brandName?: string; // 🟢 NEW: Dynamically accepts the brand's name
}

export const SizeGuideModal: React.FC<SizeGuideModalProps> = ({ 
  isOpen, 
  onClose,
  brandName
}) => {
  const [unit, setUnit] = useState<'cm' | 'in'>('cm');
  const [category, setCategory] = useState<'tops' | 'bottoms' | 'shoes'>('tops');

  if (!isOpen) return null;

  // Conversion helper
  const convert = (val: number) => {
    if (category === 'shoes') return val;
    return unit === 'cm' ? val : Math.round(val * 0.3937 * 10) / 10;
  };

  const renderTable = () => {
    if (category === 'tops') {
      return (
        <table className="w-full text-left text-sm text-white">
          <thead>
            <tr className="border-b border-zinc-800 text-zinc-500 text-[10px] uppercase tracking-widest font-black">
              <th className="py-4">Size</th>
              <th className="py-4">Chest ({unit})</th>
              <th className="py-4">Length ({unit})</th>
              <th className="py-4">Shoulder ({unit})</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-900 font-mono text-xs">
            <tr className="hover:bg-zinc-900 transition-colors"><td className="py-5 font-black text-white font-sans text-sm tracking-widest">XS</td><td>{convert(96)}</td><td>{convert(68)}</td><td>{convert(44)}</td></tr>
            <tr className="hover:bg-zinc-900 transition-colors"><td className="py-5 font-black text-white font-sans text-sm tracking-widest">S</td><td>{convert(102)}</td><td>{convert(70)}</td><td>{convert(46)}</td></tr>
            <tr className="hover:bg-zinc-900 transition-colors"><td className="py-5 font-black text-white font-sans text-sm tracking-widest">M</td><td>{convert(108)}</td><td>{convert(72)}</td><td>{convert(48)}</td></tr>
            <tr className="hover:bg-zinc-900 transition-colors"><td className="py-5 font-black text-white font-sans text-sm tracking-widest">L</td><td>{convert(114)}</td><td>{convert(74)}</td><td>{convert(50)}</td></tr>
            <tr className="hover:bg-zinc-900 transition-colors"><td className="py-5 font-black text-white font-sans text-sm tracking-widest">XL</td><td>{convert(120)}</td><td>{convert(76)}</td><td>{convert(52)}</td></tr>
          </tbody>
        </table>
      );
    }
    
    if (category === 'bottoms') {
      return (
        <table className="w-full text-left text-sm text-white">
          <thead>
            <tr className="border-b border-zinc-800 text-zinc-500 text-[10px] uppercase tracking-widest font-black">
              <th className="py-4">Size</th>
              <th className="py-4">Waist ({unit})</th>
              <th className="py-4">Hips ({unit})</th>
              <th className="py-4">Inseam ({unit})</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-900 font-mono text-xs">
            <tr className="hover:bg-zinc-900 transition-colors"><td className="py-5 font-black text-white font-sans text-sm tracking-widest">30 (S)</td><td>{convert(76)}</td><td>{convert(96)}</td><td>{convert(81)}</td></tr>
            <tr className="hover:bg-zinc-900 transition-colors"><td className="py-5 font-black text-white font-sans text-sm tracking-widest">32 (M)</td><td>{convert(81)}</td><td>{convert(101)}</td><td>{convert(81)}</td></tr>
            <tr className="hover:bg-zinc-900 transition-colors"><td className="py-5 font-black text-white font-sans text-sm tracking-widest">34 (L)</td><td>{convert(86)}</td><td>{convert(106)}</td><td>{convert(81)}</td></tr>
            <tr className="hover:bg-zinc-900 transition-colors"><td className="py-5 font-black text-white font-sans text-sm tracking-widest">36 (XL)</td><td>{convert(91)}</td><td>{convert(111)}</td><td>{convert(81)}</td></tr>
          </tbody>
        </table>
      );
    }
    
    if (category === 'shoes') {
      return (
        <table className="w-full text-left text-sm text-white">
          <thead>
            <tr className="border-b border-zinc-800 text-zinc-500 text-[10px] uppercase tracking-widest font-black">
              <th className="py-4">UK</th>
              <th className="py-4">US</th>
              <th className="py-4">EU</th>
              <th className="py-4">CM</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-900 font-mono text-xs">
            <tr className="hover:bg-zinc-900 transition-colors"><td className="py-5 font-black text-white font-sans text-sm tracking-widest">6</td><td>7</td><td>40</td><td>25.0</td></tr>
            <tr className="hover:bg-zinc-900 transition-colors"><td className="py-5 font-black text-white font-sans text-sm tracking-widest">7</td><td>8</td><td>41</td><td>25.7</td></tr>
            <tr className="hover:bg-zinc-900 transition-colors"><td className="py-5 font-black text-white font-sans text-sm tracking-widest">8</td><td>9</td><td>42</td><td>26.4</td></tr>
            <tr className="hover:bg-zinc-900 transition-colors"><td className="py-5 font-black text-white font-sans text-sm tracking-widest">9</td><td>10</td><td>43</td><td>27.1</td></tr>
            <tr className="hover:bg-zinc-900 transition-colors"><td className="py-5 font-black text-white font-sans text-sm tracking-widest">10</td><td>11</td><td>44</td><td>27.8</td></tr>
          </tbody>
        </table>
      );
    }
  };

  return (
    <div className="fixed inset-0 z-[400] bg-black/90 backdrop-blur-sm flex flex-col justify-end lg:justify-center lg:items-center animate-in fade-in duration-200">
      {/* Clickable Backdrop */}
      <div className="absolute inset-0" onClick={onClose} />

      <div className="relative w-full lg:max-w-2xl h-[85vh] lg:h-[80vh] bg-black lg:border border-zinc-800 rounded-t-[2rem] lg:rounded-none flex flex-col shadow-2xl animate-in slide-in-from-bottom-full lg:zoom-in-95 duration-300">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 lg:p-8 border-b border-zinc-800 bg-black rounded-t-[2rem] lg:rounded-none shrink-0 z-10">
          <div className="flex items-center gap-3">
              <Ruler className="text-zinc-500" size={24} />
              <div>
                  <h2 className="text-xl font-black text-white uppercase italic tracking-tighter leading-none">
                    Dimensions
                  </h2>
                  {/* 🟢 BRAND NAME DISPLAY */}
                  <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest mt-1">
                      {brandName ? `${brandName} Specifications` : 'Global Specifications'}
                  </p>
              </div>
          </div>
          
          <div className="flex items-center gap-4">
            {category !== 'shoes' && (
              <div className="bg-zinc-950 rounded-full lg:rounded-none p-1 flex border border-zinc-800">
                <button 
                  onClick={() => setUnit('cm')} 
                  className={`px-4 py-1.5 rounded-full lg:rounded-none text-[10px] font-black uppercase tracking-widest transition-all ${unit === 'cm' ? 'bg-white text-black shadow-md' : 'text-zinc-500 hover:text-white'}`}
                >
                  CM
                </button>
                <button 
                  onClick={() => setUnit('in')} 
                  className={`px-4 py-1.5 rounded-full lg:rounded-none text-[10px] font-black uppercase tracking-widest transition-all ${unit === 'in' ? 'bg-white text-black shadow-md' : 'text-zinc-500 hover:text-white'}`}
                >
                  IN
                </button>
              </div>
            )}
            <button onClick={onClose} className="w-10 h-10 bg-zinc-900 rounded-full lg:rounded-none flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors">
              <X size={18}/>
            </button>
          </div>
        </div>
        
        {/* Category Tabs */}
        <div className="flex border-b border-zinc-800 shrink-0 bg-black">
          {(['tops', 'bottoms', 'shoes'] as const).map((cat) => (
            <button 
              key={cat} 
              onClick={() => setCategory(cat)}
              className={`flex-1 py-5 text-[10px] font-black uppercase tracking-[0.2em] transition-colors ${
                category === cat 
                  ? 'text-white border-b-2 border-white bg-zinc-950' 
                  : 'text-zinc-600 hover:text-white hover:bg-zinc-900/50'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
        
        {/* Table Content */}
        <div className="flex-1 overflow-y-auto p-6 lg:p-8 bg-black scrollbar-hide">
          {renderTable()}
          
          <div className="mt-12 pt-8 border-t border-zinc-800 text-[10px] uppercase tracking-widest leading-relaxed">
            <p className="mb-6 font-mono text-zinc-400">
              <strong className="text-white">System Note:</strong> These are standard {brandName ? brandName : 'network'} sizes. 
              Streetwear silhouettes often feature relaxed or oversized cuts. When in doubt, scale up.
            </p>
            <p className="mb-4 text-zinc-500 font-black">
              Measurement Protocol:
            </p>
            <ul className="space-y-3 font-mono text-zinc-400 border-l-2 border-zinc-800 pl-4">
              <li><span className="text-white">Chest:</span> Fullest circumference of torso</li>
              <li><span className="text-white">Waist:</span> Natural waistline circumference</li>
              <li><span className="text-white">Hips:</span> Fullest circumference of lower body</li>
              <li><span className="text-white">Inseam:</span> Length from crotch seam to ankle</li>
            </ul>
          </div>
        </div>

      </div>
    </div>
  );
};